// CS Import — search the commonsku archive (CEO spec 2026-10-16; design:
// other-cs-history prototype). Read-only: results open archived projects; there
// is no action here that creates or changes anything in Flow.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Archive, Search } from "lucide-react";
import { api } from "@/lib/api";
import { CS_DOC_LABEL, CS_DOC_ORDER, csDay, type CsClientHit, type CsProjectHit, type CsStatus } from "@/lib/csArchive";
import { CsNotReady } from "./parts";

const PAGE = 25;

export function CsImportSearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const client = params.get("client") ?? "";
  const clientName = params.get("clientName") ?? "";
  const type = params.get("type") ?? "";
  const year = params.get("year") ?? "";
  const [text, setText] = useState(q);
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setText(q), [q]);
  useEffect(() => setLimit(PAGE), [q, client, type, year]);

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    setParams(next, { replace: true });
  };

  // "/" focuses the search box, like the prototype.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        document.getElementById("cs-search")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Search as you type (short pause).
  useEffect(() => {
    if (text === q) return;
    const t = setTimeout(() => set({ q: text.trim() }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const status = useQuery({ queryKey: ["cs-archive", "status"], queryFn: () => api<CsStatus>("/cs-archive/status") });
  const qs = useMemo(() => new URLSearchParams({ ...(q && { q }), ...(client && { client }), ...(type && { type }), ...(year && { year }), limit: String(limit) }).toString(), [q, client, type, year, limit]);
  const results = useQuery({
    queryKey: ["cs-archive", "search", qs],
    queryFn: () => api<{ ready: boolean; total: number; projects: CsProjectHit[] }>(`/cs-archive/search?${qs}`),
    enabled: status.data?.ready === true,
    placeholderData: (prev) => prev,
  });

  const filtered = !!(q || client || type || year);
  if (status.data && !status.data.ready) return <CsNotReady />;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-1 pb-16">
      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-medium uppercase tracking-[0.14em] text-muted">commonsku history</p>
          <h1 className="mt-3 text-[44px] font-semibold leading-[1.05] tracking-[-1.5px] text-ink">Everything worth keeping.</h1>
          <p className="mt-3 text-[17px] text-muted">Find the past project. Keep the relationship moving.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/cs-import/log" className="text-sm text-muted underline-offset-4 hover:text-ink hover:underline">Import log</Link>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-canvas px-3 py-1.5 text-sm text-ink"><Archive className="h-4 w-4" aria-hidden />Read-only archive</span>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-canvas px-5 py-4 text-[15px]">
        <p className="text-ink"><span className="font-semibold">Your history, in one place.</span> <span className="text-muted">Original projects, documents and artwork from commonsku — separate from your live business.</span></p>
        <p className="text-sm text-muted">No automatic deletion</p>
      </div>

      <label className="mt-6 flex items-center gap-3 rounded-lg border-2 border-rule bg-white px-5 focus-within:border-ink">
        <Search className="h-5 w-5 text-muted" aria-hidden />
        <input
          id="cs-search" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Search clients, contacts, projects, products, suppliers or document numbers…"
          className="h-16 w-full bg-transparent text-[18px] text-ink outline-none placeholder:text-muted"
        />
        <kbd className="rounded border border-rule px-2 py-0.5 text-xs text-muted">/</kbd>
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ClientFilter value={client} name={clientName} onChange={(id, name) => set({ client: id, clientName: name })} />
        <select value={type} onChange={(e) => set({ type: e.target.value })} className="h-11 rounded-md border border-rule bg-white px-3 text-[15px] text-ink">
          <option value="">All document types</option>
          {CS_DOC_ORDER.map((t) => <option key={t} value={t}>{CS_DOC_LABEL[t]}</option>)}
        </select>
        <select value={year} onChange={(e) => set({ year: e.target.value })} className="h-11 rounded-md border border-rule bg-white px-3 text-[15px] text-ink">
          <option value="">Any year</option>
          {(status.data?.years ?? []).map((y) => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        {filtered ? <button type="button" onClick={() => { setText(""); setParams(new URLSearchParams(), { replace: true }); }} className="text-[15px] font-semibold text-ink">Clear filters</button> : null}
        <p className="ml-auto text-sm text-muted">
          {status.data?.ready ? `${(status.data.clients ?? 0).toLocaleString()} clients · ${(status.data.projects ?? 0).toLocaleString()} projects · ${(status.data.documents ?? 0).toLocaleString()} documents` : ""}
        </p>
      </div>

      <div className="mt-9 flex items-baseline justify-between border-b border-rule pb-3">
        <h2 className="text-[20px] font-semibold text-ink">Project history <span className="text-[15px] font-normal text-muted">({results.data?.total?.toLocaleString() ?? "…"})</span></h2>
        <p className="text-sm text-muted">Newest first</p>
      </div>

      {results.isError ? <p className="py-10 text-[15px] text-red-700">The archive couldn't be searched just now. Try again.</p> : null}
      {results.data && results.data.projects.length === 0 ? (
        <p className="py-14 text-center text-[16px] text-muted">{filtered ? "Nothing in the archive matches. Try fewer words or clear a filter." : "The archive is empty — nothing has been imported yet."}</p>
      ) : null}
      <ul>
        {(results.data?.projects ?? []).map((p) => (
          <li key={p.id} className="flex items-center gap-6 border-b border-rule py-6">
            <div className="hidden h-[84px] w-[84px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-canvas sm:flex">
              {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="h-full w-full object-contain" loading="lazy" /> : <Archive className="h-6 w-6 text-muted" aria-hidden />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-muted">{p.client_name} / #{p.job_number}</p>
              <Link to={`/cs-import/projects/${p.id}`} className="mt-1 block truncate text-[21px] font-semibold text-ink hover:underline">{p.name}</Link>
              <p className="mt-1 text-[15px] text-muted">{[p.contact_name, p.client_rep_name, csDay(p.last_activity_on)].filter((x) => x && x !== "—").join(" · ")}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CS_DOC_ORDER.filter((t) => p.doc_types.includes(t)).map((t) => (
                  <span key={t} className="rounded-md border border-rule px-2.5 py-1 text-[13px] text-ink">{CS_DOC_LABEL[t]}</span>
                ))}
              </div>
              {p.matched.length ? (
                <p className="mt-2 text-[14px] text-muted">
                  Matches in {p.matched.slice(0, 6).map((m, i) => (
                    <span key={m.id}>{i ? ", " : ""}<Link className="text-ink underline-offset-2 hover:underline" to={`/cs-import/projects/${p.id}?doc=${m.id}`}>{CS_DOC_LABEL[m.doc_type]} {m.number}</Link></span>
                  ))}{p.matched.length > 6 ? ` and ${p.matched.length - 6} more` : ""}
                </p>
              ) : null}
            </div>
            <div className="shrink-0 text-right">
              <Link to={`/cs-import/projects/${p.id}`} className="inline-flex h-11 items-center rounded-md border border-rule bg-white px-4 text-[15px] font-medium text-ink hover:bg-canvas">View project ↗</Link>
              <p className="mt-2 text-[13px] text-muted">{p.document_count} document{p.document_count === 1 ? "" : "s"}</p>
            </div>
          </li>
        ))}
      </ul>
      {results.data && results.data.projects.length < results.data.total ? (
        <div className="mt-6 text-center">
          <button type="button" onClick={() => setLimit((l) => l + PAGE)} className="h-11 rounded-md border border-rule bg-white px-5 text-[15px] font-medium text-ink hover:bg-canvas">
            Show more ({(results.data.total - results.data.projects.length).toLocaleString()} left)
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Typeahead — there can be thousands of historical clients. */
function ClientFilter({ value, name, onChange }: { value: string; name: string; onChange: (id: string, name: string) => void }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const hits = useQuery({
    queryKey: ["cs-archive", "clients", text],
    queryFn: () => api<{ clients: CsClientHit[] }>(`/cs-archive/clients?q=${encodeURIComponent(text)}`),
    enabled: open,
  });
  if (value) {
    return (
      <span className="inline-flex h-11 items-center gap-2 rounded-md border border-ink bg-white px-3 text-[15px] text-ink">
        {name || "One client"}
        <button type="button" aria-label="Show all clients" onClick={() => onChange("", "")} className="text-muted hover:text-ink">×</button>
      </span>
    );
  }
  return (
    <div className="relative">
      <input
        value={text} onChange={(e) => { setText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="All clients" className="h-11 w-56 rounded-md border border-rule bg-white px-3 text-[15px] text-ink placeholder:text-ink"
      />
      {open && (hits.data?.clients?.length ?? 0) > 0 ? (
        <ul className="absolute z-20 mt-1 max-h-80 w-80 overflow-auto rounded-md border border-rule bg-white py-1 shadow-lg">
          {hits.data!.clients.map((c) => (
            <li key={c.id}>
              <button type="button" onMouseDown={() => { onChange(c.id, c.name); setText(""); setOpen(false); }} className="block w-full px-3 py-2 text-left text-[15px] text-ink hover:bg-canvas">
                {c.name}<span className="ml-2 text-[13px] text-muted">{c.project_count} project{c.project_count === 1 ? "" : "s"}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
